import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Empty } from '@pattern-library/result';
import { DataGridContext } from '../DataGrid/context';
import { sortRows } from '../utils/data-management';
import ImmutableSet from '../utils/ImmutableSet';
import { columnPropType } from '../utils/propTypes';
import { setSort } from '../stateReducer/actions';
import GridListRow from './GridListRow';
import GridListHeader from './GridListHeader';
const propTypes = {
  /**
   * Custom class to add to the container element
   */
  className: PropTypes.string,
  /**
   * Array of columns that define the layout and presentation of data in the list.
   *
   * __Note__: If you are using GridList with a `<DataGrid />`
   * component, this prop should be passed to the `<DataGrid />`
   * instead.
   */
  columns: PropTypes.arrayOf(columnPropType),
  /**
   * Data to display in the list.
   *
   * __Note__: If you are using GridList with a `<DataGrid />`
   * component, this prop should be passed to the `<DataGrid />`
   * instead.
   */
  data: PropTypes.arrayOf(PropTypes.object),
  /**
   * Sets the default sort order
   *
   * __Note__: If you are using GridList with a `<DataGrid />`
   * component, this prop should be passed to the `<DataGrid />`
   * instead.
   */
  defaultSort: PropTypes.shape({
    key: PropTypes.string.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired
  }),
  /**
   * Message to be shown when there are no rows to display
   */
  emptyMessage: PropTypes.string,
  /**
   * For applying custom classNames to a row in the list. Receives the row's
   * data object as the only argument.
   */
  getRowClass: PropTypes.func,
  /**
   * A function to get a unique ID for a row. Receives the row's data object
   * as the only argument.
   */
  getRowId: PropTypes.func,
  /**
   * Will be called whenever the `GridList` renders. Called with two arguments:
   * `filteredRows` and `visibleRows`.
   *
   * If you are using this with a `<DataGrid />` component, `filteredRows` will
   * be an array of all data object in the current filter, and `visibleRows`
   * will be an array of data objects that are currently displayed on the page.
   *
   * If you are using this as a standalone component, both arguments will be
   * the same array.
   */
  onRenderRows: PropTypes.func,
  /**
   * When row selection is enabled, this callback will be called with an array
   * of IDs of the currently selected rows.
   */
  onSelectRows: PropTypes.func,
  /**
   * Adds new custom sort functions to the GridList.
   *
   * See `DataGrid` propTypes for more information.
   *
   * __Note__: If you are using GridList with a `<DataGrid />`
   * component, this prop should be passed to the `<DataGrid />`
   * instead.
   */
  sortTypes: PropTypes.objectOf(PropTypes.func),
  /**
   * Text to be used in the table caption for accessibility
   */
  tableCaption: PropTypes.string
};
const defaultProps = {
  emptyMessage: 'There aren’t any results that match all of your filters.',
  getRowId: data => data.id
};
export default class GridList extends Component {
  constructor(props) {
    super(props);
    this.handleSortChange = (dataKey, nextSortOrder) => {
      const nextState = {
        key: dataKey,
        order: nextSortOrder
      };
      if (this.context) {
        const {
          dispatch
        } = this.context;
        dispatch(setSort(nextState));
      } else {
        this.setState({
          activeSort: nextState
        });
      }
    };
    this.handleToggleSelectRow = (rowId, nextIsSelected) => {
      const {
        onSelectRows
      } = this.props;
      if (nextIsSelected) {
        this.setState(state => ({
          selectedRows: state.selectedRows.add(rowId)
        }), () => onSelectRows(this.state.selectedRows.elements));
      } else {
        this.setState(state => ({
          selectedRows: state.selectedRows.remove(rowId)
        }), () => onSelectRows(this.state.selectedRows.elements));
      }
    };
    this.handleToggleSelectAll = nextIsSelected => {
      const {
        data,
        getRowId,
        onSelectRows
      } = this.props;
      const rows = this.context ? this.context.filteredRows : data;
      const rowIds = rows.map(getRowId);
      if (nextIsSelected) {
        this.setState(state => ({
          selectedRows: state.selectedRows.union(rowIds)
        }), () => onSelectRows(this.state.selectedRows.elements));
      } else {
        this.setState(state => ({
          selectedRows: state.selectedRows.subtract(rowIds)
        }), () => onSelectRows(this.state.selectedRows.elements));
      }
    };
    this.state = {
      selectedRows: new ImmutableSet(),
      activeSort: this.context ? undefined : this.props.defaultSort
    };
  }
  get currentState() {
    if (!this.context) {
      return {
        ...this.props,
        ...this.state,
        filteredRows: this.props.data
      };
    }
    const {
      sort: activeSort,
      pagination,
      pageCount,
      visibleRows,
      filteredRows,
      columns
    } = this.context;
    const currentPage = pagination ? pagination.currentPage : 1;
    return {
      ...this.props,
      activeSort,
      data: visibleRows,
      columns,
      filteredRows,
      currentPage,
      pageCount
    };
  }
  sortRows(rows) {
    if (this.context) {
      return rows;
    }
    const {
      data,
      columns,
      sortTypes
    } = this.props;
    const {
      activeSort
    } = this.state;
    return sortRows(data, activeSort, columns, sortTypes);
  }
  get allRowsSelected() {
    const {
      data,
      getRowId
    } = this.props;
    const {
      selectedRows
    } = this.state;
    const rows = this.context ? this.context.filteredRows : data;
    return selectedRows.containsAll(rows.map(getRowId));
  }
  isRowSelected(row) {
    const {
      getRowId
    } = this.props;
    const {
      selectedRows
    } = this.state;
    return selectedRows.contains(getRowId(row));
  }
  getScreenReaderCaption(totalRowCount) {
    const {
      emptyMessage,
      tableCaption
    } = this.props;
    const {
      columns,
      activeSort,
      currentPage,
      pageCount
    } = this.currentState;
    if (totalRowCount === 0) {
      return emptyMessage;
    }
    let caption = `${tableCaption || 'Table'} with ${totalRowCount} rows`;
    if (pageCount !== undefined && pageCount > 1) {
      caption += `, page ${currentPage + 1} of ${pageCount} pages`;
    }
    if (activeSort) {
      const order = activeSort.order === 'asc' ? 'ascending' : 'descending';
      const column = columns.find(column => column.dataKey === activeSort.key);
      const columnHeader = column ? column.header : activeSort.key;
      caption += `, sorted by ${columnHeader} column in ${order} order`;
    }
    return caption;
  }
  render() {
    const {
      className,
      emptyMessage,
      getRowId,
      getRowClass,
      onRenderRows
    } = this.props;
    const {
      columns,
      data,
      filteredRows,
      activeSort
    } = this.currentState;
    const rows = this.sortRows(data);
    onRenderRows && onRenderRows(filteredRows, rows);
    return /*#__PURE__*/React.createElement("div", {
      className: className
    }, /*#__PURE__*/React.createElement("div", null, data.length > 0 ? /*#__PURE__*/React.createElement("div", {
      role: "table"
    }, /*#__PURE__*/React.createElement(GridListHeader, {
      columns: columns,
      sortState: activeSort,
      allRowsSelected: this.allRowsSelected,
      onSortChange: this.handleSortChange,
      onToggleSelectAll: this.handleToggleSelectAll
    }), /*#__PURE__*/React.createElement("ul", {
      className: "asc-grid-list",
      role: "rowgroup",
      "data-qa": "grid-list-results"
    }, rows.map(row => /*#__PURE__*/React.createElement(GridListRow, {
      key: getRowId(row),
      columns: columns,
      data: row,
      rowId: getRowId(row),
      getRowClass: getRowClass,
      isSelected: this.isRowSelected(row),
      onToggleSelectRow: this.handleToggleSelectRow
    })))) : /*#__PURE__*/React.createElement(Empty, {
      className: "asc-grid-list-empty",
      title: emptyMessage,
      smallLayout: true
    })), /*#__PURE__*/React.createElement("div", {
      className: "asc-acc-hidden",
      "aria-live": "polite",
      "aria-atomic": "true"
    }, this.getScreenReaderCaption(rows.length)));
  }
}
GridList.propTypes = propTypes;
GridList.defaultProps = defaultProps;
GridList.contextType = DataGridContext;